import React, { useState } from 'react';
import moment from 'moment';
import {
  Popconfirm, Popover, Icon,
} from 'antd';
import OptimiseProgress from './OptimiseProgress';
import {
  SubNavigationBar, SubNavButton, SubNavIcon, WeekSequenceIndicator,
} from './styles';
import WeekPicker from '../WeekPicker';
import ScenarioPicker from '../ScenarioPicker';
import PopoverOptimisePanel from './PopoverOptimisePanel';
import { filterScenarios } from '../../helpers';
import {
  PUBLISH_CONFIRMATION, OPTIMISE_PREPARE,
  OPTIMISE_PROGRESS, OPTIMISE_SUCCESS, OPTIMISE_FAIL, OPTIMISE_CANCELLED,
  OPTIMISE_CANCEL_CONFIRMATION
} from '../../constants/text.constants';
import { SCENARIO_REPLAN_REVIEW_ENUM } from '../../constants/app.constants';
import { initialDateObject } from '../../forms/scenarioCreate';


const NButton = props => (
  <SubNavButton
    color="greyscale.5"
    mr={1}
    type="button"
    {...props}
  />
);

const SubNavigation = (props) => {
  const {
    config,
    scenarios = [],
    persistParams,
    optimise,
    optimiseStatus,
    optimiseIssues,
    waitingOptimiseStatus,
    activePlanOtherWeekIsOptimising,
    onClickToShowOptResult,
    onClickToOptimise,
    onClickToPublish,
    onClickToCancel,
    onClickToUpdateLink,
    onClickOpenRelocatableDrawer,
    publishStatus,
    cancelStatus,
    isCopyShifts,
    // experimental,
  } = props;
  const { publishingDestination, timeZone, siteName } = config;
  const disablePublish = (
    !publishingDestination
    || publishingDestination === ''
    || persistParams.scenario !== '0'
    || publishStatus
  );
  const isOptimiseRunning = (optimiseStatus === 'RUNNING' || optimiseStatus === 'PREPARING' || activePlanOtherWeekIsOptimising);
  const showOptOtherWeek = (optimiseStatus === 'SUCCEEDED' || optimiseStatus === 'FAILED' || optimiseStatus === 'CANCELLED' || !optimiseStatus)
    && activePlanOtherWeekIsOptimising;
  const viewOptimiseStatus = {
    CHECKING: {},
    PREPARING: { bg: 'clientSecondary', label: OPTIMISE_PREPARE, icon: 'info-circle' },
    RUNNING: { bg: 'clientSecondary', label: OPTIMISE_PROGRESS, icon: 'info-circle' },
    SUCCEEDED: { bg: '#090', label: OPTIMISE_SUCCESS, icon: 'check-circle' },
    FAILED: { bg: '#900', label: OPTIMISE_FAIL, icon: 'close-circle' },
    CANCELLED: { bg: '#900', label: OPTIMISE_CANCELLED, icon: 'close-circle' },
  };

  const optDisabled = isOptimiseRunning
    || waitingOptimiseStatus
    || optimise.replanScenarioState === SCENARIO_REPLAN_REVIEW_ENUM;

  const format = 'YYYY-MM-DD';
  const { start: startWeek } = initialDateObject; // current week, this is default start date
  const startOfWeekDateOrFallback = persistParams && persistParams.startOfWeekDate ? persistParams.startOfWeekDate : startWeek;
  /* startOfWeekDate is persistent user selected week(stored in state),
  this is set after manually selecting date, not to be confused for startWeek */

  const [datePickerValue, setDatePickerValue] = useState({ ...initialDateObject, start: startOfWeekDateOrFallback });
  // startOfWeekDate is assign fallback value of the current week date start

  const weekSequence = () => {
    const nextWeek = moment(startWeek).add(1, 'week').format(format); // a week ahead current week
    const prevWeek = moment(startWeek).subtract(1, 'week').format(format); // a week behind currrent week
    if (startWeek === datePickerValue.start) {
      return { color: 'success', text: 'Current Week' };
      // show this if week selected is the current week
    } if (datePickerValue.start === prevWeek) {
      return { color: 'red', text: 'Past Week' };
      // show this if week is selected is behind current week
    } if (datePickerValue.start === nextWeek) {
      return { color: 'clientSecondary', text: 'Next Week' };
      // show this if week selected is ahead of the current week
    } if (datePickerValue.start > nextWeek) {
      return { color: 'clientSecondary', text: 'Future Week' };
      // show this if week selected is many weeks ahead current week
    } if (datePickerValue.start < prevWeek) {
      return { color: 'red', text: 'Historical Data' };
      // show this if week selected is many weeks behind current week
    }
    return { color: 'clientSecondary', text: 'Unknown' };
  };

  const { color, text } = weekSequence();

  return (
    <SubNavigationBar p={1} bg="greyscale.6">
      <section>
        <ScenarioPicker
          active={persistParams.scenario}
          scenarios={filterScenarios(scenarios, persistParams)}
          onChange={id => (id === 0)
            ? onClickToUpdateLink(
              {
                scenario: id,
              },
            )
            : onClickToUpdateLink(
              {
                scenario: id,
                startOfWeekDate: scenarios.find(item => (
                  item.id === id
                )).fromDate,
              },
            )
          }
          disabled={waitingOptimiseStatus}
        />

        <WeekPicker
          ml={1}
          disabled={waitingOptimiseStatus || parseInt(persistParams.scenario, 0) !== 0}
          startDate={persistParams.startOfWeekDate}
          onChange={(d) => {
            setDatePickerValue(d);
            onClickToUpdateLink(
              {
                startOfWeekDate: d.start,
                range: persistParams.range,
                offset: persistParams.offset,
              },
            );
          }
          }
        />
        <WeekSequenceIndicator bg={color}>{text}</WeekSequenceIndicator>
      </section>
      <section>
        {
          (
            optimiseStatus === 'SUCCEEDED'
            && optimise
            && optimise.replanScenarioState === SCENARIO_REPLAN_REVIEW_ENUM
          ) && (
            <NButton
              bg={isCopyShifts ? 'greyscale.1' : 'clientSecondary'}
              onClick={!isCopyShifts && onClickToShowOptResult}
              disabled={isCopyShifts}
            >
              {isCopyShifts && (
                <SubNavIcon
                  type="sync"
                  spin
                  mr={1}
                />
              )}
              {isCopyShifts ? 'Copying to Active Plan' : 'View replan result'}
            </NButton>
          )
        }

        {
          (optimiseStatus && optimise) && (
            <Popover
              placement="bottomRight"
              title={`Optimise progress ${optimise.createdAt ? `- started at ${optimise.createdAt}` : ''}`}
              trigger="click"
              overlayClassName="DCRPOptimisePopover"
              content={(
                <OptimiseProgress
                  optimise={optimise}
                  timeZone={timeZone}
                  optimiseIssues={optimiseIssues}
                  onClickOpenRelocatableDrawer={onClickOpenRelocatableDrawer}
                  isOptimiseRunning={isOptimiseRunning}
                />
              )}
            >
              <NButton
                bg={viewOptimiseStatus[optimiseStatus].bg}
              >
                <Icon type={viewOptimiseStatus[optimiseStatus].icon} />
                {viewOptimiseStatus[optimiseStatus].label}
                {optimise.createdAt && (
                  <>
                    &nbsp;&nbsp;
                    <small>
                      (
                      {optimise.createdAt}
                      )
                    </small>
                  </>
                )}
              </NButton>
            </Popover>
          )
        }

        <PopoverOptimisePanel timeZone={timeZone} onConfirm={onClickToOptimise} siteName={siteName}>
          <NButton
            bg={optDisabled ? 'greyscale.1' : 'clientPrimary'}
            disabled={optDisabled}
          >
            {
              isOptimiseRunning
                ? <>
                  <SubNavIcon
                    type="sync"
                    spin
                    mr={1}
                  />
                  {`Optimising${showOptOtherWeek ? ' other week ' : ''}...`}
                </>
                : <>Optimise</>
            }
          </NButton>
        </PopoverOptimisePanel>

        <Popconfirm
          onConfirm={onClickToPublish}
          title={PUBLISH_CONFIRMATION}
          placement="bottomRight"
        >
          <NButton
            bg={disablePublish ? 'greyscale.1' : 'clientPrimary'}
            width={publishStatus ? '125px' : '75px'}
            disabled={disablePublish}
          >
            {
              publishStatus
                ? <>
                  <SubNavIcon
                    type="sync"
                    spin
                    mr={1}
                  />
                  Publishing...
                </>
                : <>Publish</>
            }
          </NButton>
        </Popconfirm>

        <Popconfirm
          onConfirm={onClickToCancel}
          title={OPTIMISE_CANCEL_CONFIRMATION}
          placement="bottomRight"
        >
          <NButton
            bg={'clientPrimary'}
            width={cancelStatus ? '125px' : '75px'}
            disabled={false}
          >
            {
              cancelStatus
                ? <>
                  <SubNavIcon
                    type="sync"
                    spin
                    mr={1}
                  />
                  Canceling...
                </>
                : <>Cancel</>
            }
          </NButton>
        </Popconfirm>
      </section>
    </SubNavigationBar>
  );
};

export default SubNavigation;
